/**
 * Alarm operations for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class AlarmManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  async listAlarms(
    projectUuid: string,
    page = 1,
    size = 20,
    state?: number,
    level?: number,
    deviceUuid?: string,
  ): Promise<Record<string, any>> {
    const options: Record<string, string | number> = { projectUuid };
    if (state !== undefined) options.state = state;
    if (level !== undefined) options.level = level;
    if (deviceUuid) options.deviceUuid = deviceUuid;
    const params = { current: page, size, options: JSON.stringify(options) };
    return this.session.get('/api/cdp/alarm/page', { params });
  }

  async getAlarm(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/alarm/${uuid}`);
  }

  async acknowledgeAlarm(uuid: string, comment?: string): Promise<Record<string, any>> {
    const data: Record<string, any> = { uuid };
    if (comment) data.comment = comment;
    return this.session.post('/api/cdp/alarm/ack', { data });
  }

  async clearAlarm(uuid: string, comment?: string): Promise<Record<string, any>> {
    const data: Record<string, any> = { uuid };
    if (comment) data.comment = comment;
    return this.session.post('/api/cdp/alarm/clear', { data });
  }

  async batchAckAlarms(uuids: string[], comment?: string): Promise<Record<string, any>> {
    const data: Record<string, any> = { uuids };
    if (comment) data.comment = comment;
    return this.session.post('/api/cdp/alarm/batchAck', { data });
  }

  async batchClearAlarms(uuids: string[], comment?: string): Promise<Record<string, any>> {
    const data: Record<string, any> = { uuids };
    if (comment) data.comment = comment;
    return this.session.post('/api/cdp/alarm/batchClear', { data });
  }

  async deleteAlarm(uuid: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/cdp/alarm/${uuid}`);
  }

  async listAlarmHistory(
    projectUuid: string,
    page = 1,
    size = 20,
    startTime?: string,
    endTime?: string,
    deviceUuid?: string,
  ): Promise<Record<string, any>> {
    const options: Record<string, string> = { projectUuid };
    if (startTime) options.startTime = startTime;
    if (endTime) options.endTime = endTime;
    if (deviceUuid) options.deviceUuid = deviceUuid;
    const params = { current: page, size, options: JSON.stringify(options) };
    return this.session.get('/api/cdp/alarm_his/page', { params });
  }

  async listAlarmRules(
    projectUuid: string,
    page = 1,
    size = 20,
    name?: string,
  ): Promise<Record<string, any>> {
    const options: Record<string, string> = { projectUuid };
    if (name) options.name = name;
    const params = { current: page, size, options: JSON.stringify(options) };
    return this.session.get('/api/cdp/alarm_rule/page', { params });
  }

  async getAlarmRule(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/alarm_rule/${uuid}`);
  }

  async createAlarmRule(
    projectUuid: string,
    name: string,
    ruleType: number,
    config: Record<string, any>,
    description?: string,
  ): Promise<Record<string, any>> {
    const data = {
      data: {
        projectUuid,
        name,
        ruleType,
        config,
        description,
      },
    };
    return this.session.post('/api/cdp/alarm_rule', { data });
  }

  async updateAlarmRule(
    uuid: string,
    name?: string,
    config?: Record<string, any>,
    description?: string,
    enabled?: boolean,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { data: { uuid } };
    if (name) data.data.name = name;
    if (config) data.data.config = config;
    if (description) data.data.description = description;
    if (enabled !== undefined) data.data.enabled = enabled ? 1 : 0;
    return this.session.put('/api/cdp/alarm_rule', { data });
  }

  async deleteAlarmRule(uuid: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/cdp/alarm_rule/${uuid}`);
  }

  async enableAlarmRule(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/alarm_rule/enable/${uuid}`);
  }

  async disableAlarmRule(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/alarm_rule/disable/${uuid}`);
  }

  async listAlarmTypes(projectUuid: string, page = 1, size = 20): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/alarm_type/page', { params: { current: page, size } });
  }

  async getAlarmType(uuid: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/alarm_type/${uuid}`);
  }

  async createAlarmType(
    projectUuid: string,
    name: string,
    code: string,
    level: number,
    description?: string,
  ): Promise<Record<string, any>> {
    const data = {
      data: {
        projectUuid,
        name,
        code,
        level,
        description,
      },
    };
    return this.session.post('/api/cdp/alarm_type', { data });
  }

  async updateAlarmType(
    uuid: string,
    name?: string,
    level?: number,
    description?: string,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { data: { uuid } };
    if (name) data.data.name = name;
    if (level !== undefined) data.data.level = level;
    if (description) data.data.description = description;
    return this.session.put('/api/cdp/alarm_type', { data });
  }

  async deleteAlarmType(uuid: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/cdp/alarm_type/${uuid}`);
  }

  async getAlarmStatistics(projectUuid: string): Promise<Record<string, any>> {
    const result = await this.session.get('/api/cdp/alarm/statistics', {
      params: { projectUuid },
    });
    return result?.data ?? {};
  }

  async subscribeToAlarms(
    projectUuid: string,
    callbackUrl?: string,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { projectUuid };
    if (callbackUrl) data.callbackUrl = callbackUrl;
    return this.session.post('/api/cdp/alarm/subscribe', { data });
  }

  async unsubscribeFromAlarms(subscriptionId: string): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/alarm/unsubscribe', {
      data: { subscriptionId },
    });
  }

  // --- Extended alarm APIs ---

  async confirmAlarm(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_alarm/confirm', { data: { context: {}, data } });
  }

  async clearAlarmExtend(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_alarm/clear', { data: { context: {}, data } });
  }

  async pageAlarms(page = 1, size = 20, data?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_alarm/extend/page', {
      data: { current: page, size, ...data },
    });
  }

  async downloadAlarms(data?: Record<string, any>): Promise<Blob> {
    return this.session.request('POST', '/api/cdp/cdp_alarm/download', {
      data,
      responseType: 'blob',
    });
  }

  async downloadAlarmHistory(data?: Record<string, any>): Promise<Blob> {
    return this.session.request('POST', '/api/cdp/cdp_alarm_his/download', {
      data,
      responseType: 'blob',
    });
  }

  async getAlarmLevels(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_alarm/alarmlevel', { params });
  }

  // --- Project alarm meta APIs (resApi) ---

  async pageActiveAlarms(page = 1, size = 20, options?: Record<string, any>): Promise<Record<string, any>> {
    const params: Record<string, any> = { page, size };
    if (options) params.options = JSON.stringify(options);
    return this.session.get('/api/cdp_alarm/page', { params });
  }

  async getActiveAlarm(id: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp_alarm/get/${id}`);
  }

  async createActiveAlarm(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp_alarm/create', { data });
  }

  async updateActiveAlarm(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp_alarm/update', { data });
  }

  async deleteActiveAlarm(id: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp_alarm/delete/${id}`);
  }

  async batchDeleteActiveAlarms(ids: string[]): Promise<Record<string, any>> {
    return this.session.post('/api/cdp_alarm/batchDelete', { data: ids });
  }

  async pageHisAlarms(page = 1, size = 20, options?: Record<string, any>): Promise<Record<string, any>> {
    const params: Record<string, any> = { page, size };
    if (options) params.options = JSON.stringify(options);
    return this.session.get('/api/cdp_alarm_his/page', { params });
  }

  async getHisAlarm(id: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp_alarm_his/get/${id}`);
  }

  async createHisAlarm(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp_alarm_his/create', { data });
  }

  async updateHisAlarm(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp_alarm_his/update', { data });
  }

  async deleteHisAlarm(id: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp_alarm_his/delete/${id}`);
  }

  async batchDeleteHisAlarms(ids: string[]): Promise<Record<string, any>> {
    return this.session.post('/api/cdp_alarm_his/batchDelete', { data: ids });
  }
}
